/* Native disclosure semantics; no framework or third-party animation dependency. */
if (!customElements.get('nogo-floating-header')) {
  customElements.define('nogo-floating-header', class extends HTMLElement {
    connectedCallback() {
      // A fixed header must not live inside the clipped content scrollport.
      // Move the whole group (including announcements) before initialization.
      // Moving reconnects this element; the new callback owns initialization.
      const scrollport = this.closest('.page-wrapper');
      const headerGroup = this.closest('#header-group');
      if (scrollport && headerGroup && scrollport.contains(headerGroup) && scrollport.parentNode) {
        scrollport.before(headerGroup);
        return;
      }
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal: this.abort.signal });
      this.menu = this.querySelector('[data-nah-menu]');
      this.toggle = this.querySelector('[data-nah-toggle]');
      this.bar = this.querySelector('[data-nah-bar]');
      this.dropdowns = [...this.querySelectorAll('[data-nah-dropdown]')];
      this.mobile = matchMedia('(max-width:991px)');
      this.hover = matchMedia('(hover:hover) and (pointer:fine)');
      this.timers = new Set();
      this.locks = [];
      this.frame = 0;
      this.menuWasOpen = false;
      this.updateOverlay = () => { this.dataset.menuOpen = String(this.dataset.mobileOpen === 'true' || this.dropdowns.some(d => d.open)); };
      this.closeDropdowns = except => this.dropdowns.forEach(d => { if (d !== except) d.open = false; });
      this.unlock = () => {
        for (const [element, value] of this.locks) element.style.overflow = value;
        this.locks = [];
      };
      this.setMobile = (open, restoreFocus = false) => {
        open = Boolean(open && this.mobile.matches);
        this.dataset.mobileOpen = String(open);
        this.toggle.setAttribute('aria-expanded', String(open));
        this.toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        this.menu.inert = this.mobile.matches && !open;
        this.dataset.menuStyle = this.mobile.matches ? 'drawer' : 'menu';
        this.syncPreviewRoles?.();
        if (open && !this.locks.length) {
          const targets = [document.documentElement, document.body, document.querySelector('.page-wrapper')].filter(Boolean);
          this.locks = [...new Set(targets)].map(el => [el, el.style.overflow]);
          for (const [element] of this.locks) element.style.overflow = 'hidden';
        } else if (!open) {
          this.unlock();
          this.closeDropdowns();
          if (restoreFocus) this.toggle.focus({ preventScroll: true });
        }
        this.updateOverlay();
      };
      const normalizeTitle = value => String(value || '').normalize('NFKC').trim().toLocaleLowerCase().replace(/\s+/g, ' ');
      const previewTriggers = group => [...group.querySelectorAll('[data-nah-preview-trigger]')];
      const previewPanels = group => [...group.querySelectorAll('[data-nah-preview-panel]')];
      const originals = new Map(this.dropdowns.flatMap(group => previewPanels(group).map(panel => [panel, panel.innerHTML])));
      this.fitProducts = () => {
        if (this.mobile.matches) return;
        for (const group of this.dropdowns) {
          if (!group.open || group.dataset.layout !== 'wide') continue;
          for (const panel of previewPanels(group)) {
            if (panel.hidden) continue;
            const grid = panel.querySelector('.nah-product-grid');
            if (!grid) continue;
            const cards = [...grid.querySelectorAll('.nah-product-card')];
            if (!cards.length) continue;
            const width = grid.getBoundingClientRect().width;
            if (!width) continue;
            const target = parseFloat(getComputedStyle(panel).getPropertyValue('--nah-card-max')) || 320;
            const gap = parseFloat(getComputedStyle(grid).columnGap) || 20;
            const count = Math.min(cards.length,6,Math.max(1,Math.ceil((width + gap)/(target + gap))));
            grid.style.setProperty('--nah-card-columns',String(count));
            const start = Math.max(0,Math.min(Number(panel.dataset.nahProductStart) || 0,cards.length-count));
            panel.dataset.nahProductStart=String(start);
            panel.dataset.nahProductCount=String(count);
            cards.forEach((card,index) => { card.hidden = index < start || index >= start+count; });
            const controls=panel.querySelector('[data-nah-products-controls]');
            if(controls) {
              controls.hidden=cards.length<=count;
              controls.querySelector('[data-nah-products-step="-1"]').disabled=start===0;
              controls.querySelector('[data-nah-products-step="1"]').disabled=start+count>=cards.length;
              controls.querySelector('[data-nah-products-status]').textContent=`${start+1}–${start+count} / ${cards.length}`;
            }
          }
        }
      };
      this.activatePreview = (group, trigger, focus = false) => {
        if (!group || !trigger) return;
        const index = trigger.dataset.nahPreviewTrigger;
        for (const item of previewTriggers(group)) {
          const selected = item === trigger;
          item.classList.toggle('is-active', selected);
          if (!this.mobile.matches && group.dataset.layout === 'wide') {
            item.removeAttribute('aria-selected');
            item.removeAttribute('tabindex');
          }
        }
        for (const panel of previewPanels(group)) panel.hidden = panel.dataset.nahPreviewPanel !== index;
        this.fitProducts();
        if (focus) trigger.focus({ preventScroll: true });
      };
      this.syncPreviewRoles = () => {
        for (const group of this.dropdowns) {
          const desktop = !this.mobile.matches && group.dataset.layout === 'wide';
          const list = group.querySelector('[data-nah-preview-list]');
          list?.removeAttribute('role');
          list?.removeAttribute('aria-orientation');
          for (const trigger of previewTriggers(group)) {
            if (desktop) {
              trigger.removeAttribute('role');
              trigger.removeAttribute('aria-selected');
              trigger.removeAttribute('tabindex');
            } else { trigger.removeAttribute('role'); trigger.removeAttribute('aria-selected'); trigger.removeAttribute('tabindex'); }
          }
          for (const panel of previewPanels(group)) {
            panel.removeAttribute('role');
          }
        }
      };
      this.applyMenuSettings = () => {
        for (const [panel, content] of originals) {
          panel.innerHTML = content;
          for (const property of ['--nah-card-ratio','--nah-card-max','--nah-card-fit']) panel.style.removeProperty(property);
        }
        this.querySelectorAll('[data-nah-custom-description]').forEach(node => node.remove());
        const templates = [...this.querySelectorAll('template[data-nah-menu-settings]')].sort((a,b) => Boolean(a.dataset.nahChild) - Boolean(b.dataset.nahChild));
        for (const template of templates) {
          const group = this.dropdowns.find(item => normalizeTitle(item.dataset.nahMenuTitle) === normalizeTitle(template.dataset.nahParent));
          if (!group) continue;
          const childTitle = normalizeTitle(template.dataset.nahChild);
          if (!childTitle && template.dataset.nahLayout) group.dataset.layout = template.dataset.nahLayout;
          for (const trigger of previewTriggers(group)) {
            if (childTitle && normalizeTitle(trigger.dataset.nahSubmenuTitle) !== childTitle) continue;
            const panel = previewPanels(group).find(item => item.dataset.nahPreviewPanel === trigger.dataset.nahPreviewTrigger);
            if (!panel) continue;
            const ratio = template.dataset.nahImageRatio;
            if (['1 / 1','3 / 4','4 / 5','5 / 6'].includes(ratio)) panel.style.setProperty('--nah-card-ratio', ratio);
            const width = Number(template.dataset.nahImageWidth);
            if (width >= 180 && width <= 480) panel.style.setProperty('--nah-card-max', `${width}px`);
            const fit = template.dataset.nahImageFit;
            if (['contain','cover'].includes(fit)) panel.style.setProperty('--nah-card-fit', fit);
            if (template.dataset.nahHasOverride !== 'true') continue;
            const originalLink = panel.querySelector('.nah-view-all')?.getAttribute('href');
            panel.replaceChildren(template.content.cloneNode(true));
            if (!panel.querySelector('.nah-view-all') && originalLink) {
              const link = document.createElement('a'); link.className = 'nah-view-all'; link.href = originalLink; link.textContent = 'View all ↗'; panel.querySelector('.nah-preview-heading')?.append(link);
            }
          }
        }
        this.syncPreviewRoles();
        this.fitProducts();
      };
      this.applyMenuSettings();
      const previewFromEvent = event => {
        const trigger = event.target instanceof Element ? event.target.closest('[data-nah-preview-trigger]') : null;
        const group = trigger?.closest('[data-nah-dropdown]');
        return trigger && group && !this.mobile.matches && group.dataset.layout === 'wide' ? { trigger, group } : null;
      };
      on(this.menu, 'click', event => {
        const button = event.target instanceof Element ? event.target.closest('[data-nah-products-step]') : null;
        if(button && !button.disabled) {
          const panel=button.closest('[data-nah-preview-panel]');
          if(panel) {
            event.preventDefault();
            panel.dataset.nahProductStart=String((Number(panel.dataset.nahProductStart)||0)+Number(button.dataset.nahProductsStep)*(Number(panel.dataset.nahProductCount)||1));
            this.fitProducts();
            if(!matchMedia('(prefers-reduced-motion: reduce)').matches) panel.querySelector('.nah-product-grid')?.animate([{opacity:.55,transform:`translateX(${Number(button.dataset.nahProductsStep)*8}px)`},{opacity:1,transform:'translateX(0)'}],{duration:220,easing:'ease-out'});
          }
          return;
        }
        const pair = previewFromEvent(event);
        if (!pair || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        // Keep anchors native: hover/focus previews; click navigates to the collection.
        this.activatePreview(pair.group, pair.trigger);
      });
      on(this.menu, 'pointerover', event => { const pair = previewFromEvent(event); if (pair && this.hover.matches) this.activatePreview(pair.group, pair.trigger); });
      on(this.menu, 'focusin', event => { const pair = previewFromEvent(event); if (pair) this.activatePreview(pair.group, pair.trigger); });
      on(this.menu, 'keydown', event => {
        const pair = previewFromEvent(event); if (!pair) return;
        const triggers = previewTriggers(pair.group), index = triggers.indexOf(pair.trigger);
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % triggers.length;
        else if (event.key === 'ArrowUp') next = (index - 1 + triggers.length) % triggers.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = triggers.length - 1;
        if (next !== undefined) { event.preventDefault(); this.activatePreview(pair.group, triggers[next], true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); pair.group.querySelector('[data-nah-preview-panel]:not([hidden]) a')?.focus(); }
      });
      this.settingsObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes,...record.removedNodes].some(node => node instanceof Element && (node.matches('template[data-nah-menu-settings]') || node.querySelector('template[data-nah-menu-settings]'))))) this.applyMenuSettings();
      });
      this.settingsObserver.observe(this, { childList: true, subtree: true });
      on(this.toggle, 'click', () => this.setMobile(this.dataset.mobileOpen !== 'true'));
      on(this.querySelector('[data-nah-overlay]'), 'click', () => { this.setMobile(false, this.mobile.matches); this.closeDropdowns(); this.updateOverlay(); });
      for (const disclosure of this.dropdowns) {
        const summary = disclosure.querySelector('summary');
        let leaveTimer;
        on(disclosure, 'toggle', () => {
          if (disclosure.open) this.closeDropdowns(disclosure);
          summary.setAttribute('aria-expanded', String(disclosure.open));
          this.updateOverlay();
          this.fitProducts();
        });
        on(disclosure, 'pointerenter', () => {
          clearTimeout(leaveTimer);this.timers.delete(leaveTimer);
          if (!this.mobile.matches && this.hover.matches) { this.closeDropdowns(disclosure); disclosure.open = true; this.updateOverlay(); }
        });
        on(disclosure, 'pointerleave', () => {
          if (this.mobile.matches || !this.hover.matches) return;
          leaveTimer = setTimeout(() => { this.timers.delete(leaveTimer); if (!disclosure.contains(document.activeElement)) disclosure.open = false; }, 160);
          this.timers.add(leaveTimer);
        });
        on(summary, 'keydown', event => {
          if (event.key === 'ArrowDown') { event.preventDefault(); this.closeDropdowns(disclosure); disclosure.open = true; disclosure.querySelector('.nah-panel a')?.focus(); }
        });
      }
      on(document, 'pointerdown', event => {
        if (!this.contains(event.target)) { this.closeDropdowns(); if (this.dataset.mobileOpen === 'true') this.setMobile(false); this.updateOverlay(); }
      });
      on(this, 'focusout', event => {
        if (!this.mobile.matches && event.relatedTarget && !this.contains(event.relatedTarget)) { this.closeDropdowns(); this.updateOverlay(); }
      });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.dataset.menuOpen === 'true') {
          const opened = this.dropdowns.find(d => d.open);
          if (this.dataset.mobileOpen === 'true') this.setMobile(false, true);
          else { this.closeDropdowns(); opened?.querySelector('summary')?.focus(); }
          this.updateOverlay(); event.preventDefault();
        }
        if (event.key === 'Tab' && this.dataset.mobileOpen === 'true') {
          const focusable = [...this.querySelectorAll('a[href],button:not([disabled]),summary')].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
          const first = focusable[0], last = focusable.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault();last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault();first?.focus(); }
        }
      });
      on(this.menu, 'click', event => { if (event.defaultPrevented) return; if (event.target.closest('a[href]')) { this.setMobile(false); this.closeDropdowns(); this.updateOverlay(); } });
      on(this.mobile, 'change', () => { this.setMobile(false); this.measure(); });
      on(document, 'shopify:block:select', event => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target || !this.contains(target)) return;
        if (this.mobile.matches && this.menu.contains(target)) this.setMobile(true);
        const dropdown = target.matches('[data-nah-dropdown]') ? target : target.querySelector('[data-nah-dropdown]') || target.closest('[data-nah-dropdown]');
        if (dropdown) { this.closeDropdowns(dropdown); dropdown.open = true; this.updateOverlay(); }
      });
      on(document, 'shopify:block:deselect', () => { this.closeDropdowns(); this.updateOverlay(); });
      this.measure = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          if (!this.isConnected) return;
          const barRect = this.bar.getBoundingClientRect();
          const height = barRect.height;
          this.style.setProperty('--nah-bar-h', `${height}px`);
          // Announcement bars before this section remain above the floating card.
          const group = this.closest('#header-group') || this.closest('.shopify-section-group-header-group')?.parentElement;
          const section = this.closest('.shopify-section');
          let offset = 0;
          if (group && section) for (const node of group.children) {
            if (node === section || node.contains(section)) break;
            const rect = node.getBoundingClientRect();
            if (rect.height > 0 && rect.bottom > 0) offset = Math.max(offset, rect.bottom);
          }
          this.style.setProperty('--nah-offset', `${Math.max(0, offset)}px`);
          const top = parseFloat(getComputedStyle(this.bar).marginTop) || 0;
          document.body.style.setProperty('--header-height', `${Math.round(top + height)}px`);
          const gap = parseFloat(getComputedStyle(this).getPropertyValue('--nah-gap')) || 0;
          const reserved = this.dataset.reserve === 'true' ? top + height + gap : 0;
          const contentTop = Math.ceil(group?.getBoundingClientRect().bottom || offset + reserved);
          document.body.style.setProperty('--header-group-height', `${contentTop}px`);
          document.body.style.setProperty('--nah-content-top', `${contentTop}px`);
          this.fitProducts();
        });
      };
      this.resize = new ResizeObserver(this.measure);
      this.resize.observe(this.bar);
      const group = this.closest('#header-group');
      if (group) for (const node of group.children) if (node !== this.closest('.shopify-section')) this.resize.observe(node);
      on(window, 'resize', this.measure);
      on(window, 'scroll', this.measure, { passive: true });
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) on(wrapper, 'scroll', this.measure, { passive: true });
      this.setMobile(false);this.measure();
    }
    disconnectedCallback() {
      this.abort?.abort();this.resize?.disconnect();this.settingsObserver?.disconnect();cancelAnimationFrame(this.frame);
      for (const timer of this.timers || []) clearTimeout(timer);
      this.unlock?.();this.abort = null;
    }
  });
}
